import { __rest } from "tslib";
import lodashGet from 'lodash/get';
import lodashChunk from 'lodash/chunk';
import { inWindow } from '@/config';
import { wait } from '@/api/util/index';
import { writeContactsToLoki, writeLxInfoToLoki } from './contact_sync_enterprise_loki';
import { dispatchContactSyncEvents } from './util/contact_notify_helper';
export class ContactDBEnterprise {
    constructor(ctx, base) {
        this.ctx = ctx;
        this.base = base;
        this.coreQuickCleanDelayDuration = 2 * 1000;
        this.coreCleanDelayDuration = 15 * 1000;
        this.coreNeedDeleteStep = new Map();
        this.needComputeMemberNum = new Map();
        this.contactTempSyncStatus = new Map();
    }
    addCoreContactLock(options) {
        var _a;
        const { tableName, enterpriseId } = options;
        const mainEnterpriseId = this.ctx.systemApi.getCurrentCompanyId(options._account);
        if (enterpriseId !== mainEnterpriseId) {
            return;
        }
        if (!this.coreNeedDeleteStep.has(options._account)) {
            this.coreNeedDeleteStep.set(options._account, new Set());
        }
        (_a = this.coreNeedDeleteStep.get(options._account)) === null || _a === void 0 ? void 0 : _a.add(tableName);
    }
    async coreContactAllInto(options, _config) {
        const { list, orgpathlist } = options, restOptions = __rest(options, ["list", "orgpathlist"]);
        const lastUpdateTime = Date.now();
        const config = Object.assign(Object.assign({}, _config), { _account: options._account, lastUpdateTime });
        const cleanDelayDuration = lodashGet(options, 'enableFastCleanInvalidContact', false) ? this.coreQuickCleanDelayDuration : this.coreCleanDelayDuration;
        if (!Array.isArray(list) || !list.length) {
            console.time('[contact_dbl]coreContactAllInto.deleteInvalidCoreContact');
            this.ctx.eventApi.sendSysEvent({
                eventName: 'subCoreOrgContactReady',
                eventData: {
                    enterpriseId: config.enterpriseId,
                    data: new Map(),
                },
                _account: restOptions._account,
            });
            setTimeout(() => {
                this.addCoreContactLock(Object.assign({ tableName: 'contact', enterpriseId: config.enterpriseId }, restOptions));
                this.deleteInvalidCoreContact(Object.assign({ lastUpdateTime, enterpriseId: config.enterpriseId }, restOptions));
            }, cleanDelayDuration);
            console.timeEnd('[contact_dbl]coreContactAllInto.deleteInvalidCoreContact');
            return;
        }
        const highDBConfig = { limitSize: 3000, intervalDuration: 100 };
        const lowDbConfig = { limitSize: 1500, intervalDuration: 300 };
        const { limitSize, intervalDuration } = this.ctx.systemApi.getCurrentCompanyId() === config.enterpriseId && restOptions.isMainAccount ? highDBConfig : lowDbConfig;
        const orgContactMapGroupbyOrg = new Map();
        const intoList = lodashChunk(list, limitSize).reduce((total, subList) => {
            const { contact: coreEnterpriseContactList, contactItem: coreEnterpriseContactItemlist, orgContact: coreEnterpriseOrgContactList, } = this.ctx.contactTrans.transformCoreContact2MultipeData(subList, orgpathlist, config);
            coreEnterpriseOrgContactList.forEach(item => {
                const { orgId } = item;
                if (!orgContactMapGroupbyOrg.has(orgId)) {
                    orgContactMapGroupbyOrg.set(orgId, 0);
                }
                orgContactMapGroupbyOrg.set(orgId, (orgContactMapGroupbyOrg.get(orgId) || 0) + 1);
            });
            total.push({
                contact: coreEnterpriseContactList,
                contactItem: coreEnterpriseContactItemlist,
                orgContact: coreEnterpriseOrgContactList,
            });
            return total;
        }, []);
        this.ctx.eventApi.sendSysEvent({
            eventName: 'subCoreOrgContactReady',
            eventData: {
                enterpriseId: config.enterpriseId,
                data: orgContactMapGroupbyOrg,
            },
            _account: restOptions._account,
        });
        const promiseRequest = intoList
            .reduce((total, current, currentIndex) => {
            total = total
                .then(async () => {
                if (currentIndex !== 0) {
                    await wait(intervalDuration);
                }
                return this.coreContactIntoChunked(Object.assign(Object.assign({}, current), restOptions));
            })
                .catch(ex => {
                console.error('[contact_dbl]coreContactIntoChunked.error', ex);
            });
            return total;
        }, Promise.resolve())
            .catch(ex => {
            console.error('[contact_dbl]coreContactAllInto.error', ex);
        });
        try {
            await promiseRequest;
        }
        catch (ex) {
            this.ctx.dataTrackerApi.track('pc_contact_sync_db_error', {
                _account: options._account,
                from: 'enterprise',
                step: 'core',
                message: lodashGet(ex, 'message', `${ex}`),
            });
        }
        await this.cleanOrgpathTable(Object.assign({}, restOptions));
        setTimeout(() => {
            this.addCoreContactLock(Object.assign({ tableName: 'contact', enterpriseId: config.enterpriseId }, restOptions));
            this.deleteInvalidCoreContact(Object.assign({ lastUpdateTime, enterpriseId: config.enterpriseId }, restOptions));
        }, cleanDelayDuration);
    }
    async coreContactIntoChunked(options) {
        const { contact: coreEnterpriseContactList, contactItem: coreEnterpriseContactItemlist, orgContact: coreEnterpriseOrgContactList } = options;
        const hasEmpty = [coreEnterpriseContactList, coreEnterpriseContactItemlist].some(item => !item || !item.length);
        if (hasEmpty) {
            return;
        }
        const requestPromiseList = [
            {
                table: this.ctx.contactUtil.contactTable,
                list: coreEnterpriseContactList,
            },
            {
                table: this.ctx.contactUtil.contactItemTable,
                list: coreEnterpriseContactItemlist,
            },
            {
                table: this.ctx.contactUtil.orgContactTable,
                list: coreEnterpriseOrgContactList,
            },
        ].map(({ table, list }) => {
            this.ctx.contactUtil.forbiddenBridgeOnce();
            return this.ctx.dbApi.putAll({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: table,
                _dbAccount: options._account,
            }, list, {
                pageSize: 5000,
                supportCache: 'disable',
            });
        });
        await Promise.all(requestPromiseList);
    }
    async coreOrgAllInto(options, config) {
        const { list } = options, restOptions = __rest(options, ["list"]);
        const { enterpriseId, visibleCode = 0, enableFastCleanInvalidContact = false } = config;
        const curEnterpriseId = enterpriseId || this.ctx.contactUtil.getCurrentCompanyId(restOptions._account);
        const lastUpdateTime = Date.now();
        const cleanDelayDuration = enableFastCleanInvalidContact ? this.coreQuickCleanDelayDuration : this.coreCleanDelayDuration;
        const _now = Date.now();
        const { fullPathList: orgFullPathList, entityOrgList, orgTreeMap, } = this.ctx.contactTrans.flattenServerUnit2FullOrgPath(list, { visibleCode: visibleCode || 0, lastUpdateTime, _account: options._account }, curEnterpriseId);
        const traverseDuration = Date.now() - _now;
        if (traverseDuration > 2000) {
            this.ctx.dataTrackerApi.track('pc_core_org_traverse', {
                _account: options._account,
                duration: Date.now() - _now,
                count: list.length,
            });
        }
        this.coreFullOrgpathAllInto(Object.assign({ orgFullPathList, enterpriseId: curEnterpriseId }, restOptions));
        if (entityOrgList && entityOrgList.length) {
            const orgcontactMapGroupbyOrgId = (await this.ctx.eventHub
                .waitForOrgContactReady(options._account, {
                filter: e => lodashGet(e.eventData, 'enterpriseId', curEnterpriseId) === curEnterpriseId,
            })
                .then(e => (e ? lodashGet(e.eventData, 'data', new Map()) : new Map())));
            const nodeMemberCountMap = await this.ctx.contactUtil.computeEntityOrgMemberCount({
                rootOrgKey: '-2',
                orgContactMap: orgcontactMapGroupbyOrgId,
                orgTreeMap,
            });
            entityOrgList.map(item => {
                const { id } = item;
                if (nodeMemberCountMap.has(id)) {
                    item.memberNum = nodeMemberCountMap.get(id).totalCount;
                }
                else {
                    item.memberNum = 0;
                }
                return item;
            });
            try {
                this.ctx.contactUtil.forbiddenBridgeOnce();
                await this.ctx.dbApi.putAll({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.orgTable,
                    _dbAccount: options._account,
                }, entityOrgList, {
                    pageSize: 3000,
                });
            }
            catch (ex) {
                this.ctx.dataTrackerApi.track('pc_contact_sync_db_error', {
                    _account: options._account,
                    from: 'org',
                    step: 'core',
                    message: lodashGet(ex, 'message', `${ex}`),
                });
            }
        }
        setTimeout(() => {
            this.addCoreContactLock({
                enterpriseId: curEnterpriseId,
                tableName: 'org',
                _account: options._account,
            });
            this.deleteInvalidEntityCoreOrg(Object.assign({ lastUpdateTime, enterpriseId: curEnterpriseId }, restOptions));
        }, cleanDelayDuration);
        return true;
    }
    async enterpriseAllInto(options) {
        const { list: _list, force = false, isMainAccount, _account } = options;
        const { util } = await import('@/api/util');
        const list = util.singleToList(_list);
        const _lastUpdateTime = Date.now();
        if (!force && list && list.length) {
            this.needComputeMemberNum.set(_account, true);
        }
        const { contactItem: enterpriseInfoList, contact: enterpriseList, orgContact: orgContactList, needDeleteContactIds = [], } = this.ctx.contactTrans.transferEnterprise2MutileTableData(list, _lastUpdateTime, {
            force: window.isAccountBg ? false : force,
        });
        const intoCountMap = new Map();
        try {
            const promiseRequest = [
                this.base.intoTable({
                    tableName: this.ctx.contactUtil.contactTable,
                    list: enterpriseList,
                    isMainAccount,
                    _account,
                }, {
                    supportCache: force ? 'disable' : 'enable',
                }),
                this.base.intoTable({
                    tableName: this.ctx.contactUtil.contactItemTable,
                    list: enterpriseInfoList,
                    isMainAccount,
                    _account,
                }),
                this.base.intoTable({
                    tableName: this.ctx.contactUtil.orgContactTable,
                    list: orgContactList,
                    isMainAccount,
                    _account,
                }),
            ];
            const intoResultList = await Promise.all(promiseRequest);
            intoResultList.forEach((list, index) => {
                if (index === 0 && enterpriseList.length > list.length) {
                    intoCountMap.set(this.ctx.contactUtil.contactTable, enterpriseList.length - list.length);
                }
                else if (index === 1 && enterpriseInfoList.length > list.length) {
                    intoCountMap.set(this.ctx.contactUtil.contactItemTable, enterpriseInfoList.length - list.length);
                }
                else if (index === 2 && orgContactList.length - list.length) {
                    intoCountMap.set(this.ctx.contactUtil.orgContactTable, orgContactList.length - list.length);
                }
            });
        }
        catch (e) {
            console.error('[contact]enterpriseAllInto.error(code:2)', list, e);
            throw e;
        }
        if (!force && Array.isArray(needDeleteContactIds) && needDeleteContactIds.length) {
            await this.cleanInvalidEnterpriseRawData({
                contactIds: needDeleteContactIds,
                isMainAccount,
                _account,
            });
        }
        if (!force && Array.isArray(enterpriseList) && enterpriseList.length) {
            await this.cleanExpiredEntityRawdata({
                list: enterpriseList,
                lastUpdateTime: _lastUpdateTime,
                isMainAccount,
                _account,
            });
        }
        if (intoCountMap.size) {
            this.ctx.dataTrackerApi.track('pc_db_put_lost', Object.assign(Object.assign({}, Object.fromEntries(intoCountMap)), { _account: options._account, enableTrackInBg: true, dbname: 'contact' }));
        }
        return Promise.resolve([
            {
                contact_enterprise: {
                    updateDiff: force ? [] : enterpriseList.map(item => item.id),
                    deleteDiff: [],
                },
                insertCountMap: Object.fromEntries(intoCountMap),
                _account,
            },
        ]);
    }
    async orgAllInto(options) {
        const { list, isMainAccount, _account } = options;
        const _lastUpdateTime = Date.now();
        const res = this.ctx.contactTrans.transOrg2EntityOrg({ list, _lastUpdateTime, _account });
        let { entityOrgList: orgList } = res;
        const { needDeleteOrgIds } = res;
        console.log('orgAllInto orgList', orgList);
        try {
            if (orgList && orgList.length) {
                const ids = orgList.map(item => item.id);
                const oldOrgList = await this.ctx.dbApi.getByIds({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.orgTable,
                    _dbAccount: options._account,
                }, ids);
                const memberNumMap = new Map();
                oldOrgList.forEach(item => {
                    if (!item) {
                        return;
                    }
                    memberNumMap.set(item.id, item.memberNum);
                });
                orgList = orgList.map(item => {
                    item.memberNum = memberNumMap.get(item.id) || 0;
                    return item;
                });
            }
        }
        catch (ex) {
            console.error('[contactDB]orgAllInto.error(0):', ex);
        }
        try {
            if (orgList && orgList.length) {
                await this.base.intoTable({
                    tableName: this.ctx.contactUtil.orgTable,
                    list: orgList,
                    isMainAccount,
                    _account,
                });
            }
        }
        catch (ex) {
            console.error('[contactDB]orgAllInto.error(1):', ex);
        }
        try {
            if (needDeleteOrgIds && needDeleteOrgIds.length) {
                await this.ctx.dbApi.deleteById({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.orgTable,
                    _dbAccount: _account,
                }, needDeleteOrgIds);
            }
        }
        catch (ex) {
            console.error('[contactDB]orgAllInto.error(2):', ex);
        }
        return [
            {
                org: {
                    deleteDiff: [],
                    updateDiff: [],
                },
                _account,
            },
        ];
    }
    orgInto(list) {
        var _a;
        const { util } = require('@/api/util');
        const { entityOrgList: orgList } = this.ctx.contactTrans.transOrg2EntityOrg({
            list,
            _lastUpdateTime: Date.now(),
            _account: ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
        });
        return util.SyncCatchError(() => {
            var _a;
            return this.base.intoTable({
                tableName: this.ctx.contactUtil.orgTable,
                list: orgList,
                isMainAccount: true,
                _account: ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
            });
        });
    }
    async coreFullOrgpathAllInto(options) {
        const { orgFullPathList, enterpriseId } = options;
        if (!orgFullPathList || !orgFullPathList.length) {
            await wait(50);
            this.ctx.eventApi.sendSysEvent({
                eventName: 'coreOrgPathlistReady',
                eventData: {
                    type: 'server',
                    enterpriseId,
                },
                _account: options._account,
            });
            return;
        }
        this.ctx.contactUtil.forbiddenBridgeOnce();
        try {
            await this.ctx.dbApi.putAll({
                dbName: this.ctx.contactUtil.contactSearchDbName,
                tableName: this.ctx.contactUtil.orgpathlistTable,
                _dbAccount: options._account,
            }, orgFullPathList, { memoryPageSize: 10000 });
            this.ctx.eventApi.sendSysEvent({
                eventName: 'coreOrgPathlistReady',
                eventData: {
                    type: 'server',
                    enterpriseId,
                },
                _account: options._account,
            });
        }
        catch (ex) {
            console.error('[contact_dbl]corOgAllInto.put.orgpathlist.error', ex);
        }
    }
    async cleanOrgpathTable(options) {
        const { tableName = this.ctx.contactUtil.orgpathlistTable } = options;
        window.bridgeApi.master.forbiddenBridgeOnce();
        return this.ctx.dbApi.clear({
            dbName: this.ctx.contactUtil.contactSearchDbName,
            tableName,
            _dbAccount: options._account,
        });
    }
    async deleteInvalidEntityCoreOrg(options) {
        const { lastUpdateTime, enterpriseId } = options;
        const timestamp = lastUpdateTime - 10;
        return this.ctx.dbApi
            .deleteByByRangeCondition({
            dbName: this.ctx.contactUtil.contactDbName,
            tableName: this.ctx.contactUtil.orgTable,
            adCondition: {
                type: 'between',
                field: ['enterpriseId', '_lastUpdateTime'],
                args: [
                    [enterpriseId, 0],
                    [enterpriseId, timestamp],
                ],
            },
            _dbAccount: options._account,
        }, options._account, { supportCache: false })
            .finally(() => {
            this.sendCoreDeleteInvalidEvent({ tableName: 'org', _account: options._account, enterpriseId });
        });
    }
    async deleteDuplicateOrgContact(params) {
        const { enterpriseId, lastUpdateTime } = params;
        const mainCompanyId = this.ctx.systemApi.getCurrentCompanyId(params._account);
        if (mainCompanyId !== enterpriseId || !this.ctx.systemApi.getIsMainAccount(params._account)) {
            return;
        }
        const totalOrgContactList = await this.ctx.dbApi.getByRangeCondition({
            dbName: this.ctx.contactUtil.contactDbName,
            tableName: this.ctx.contactUtil.orgContactTable,
            _dbAccount: params._account,
        });
        const needDeleteOrgContactIds = [];
        await lodashChunk(totalOrgContactList, 5000).reduce((total, chunkList, index) => {
            total = total.then(async () => {
                if (index > 0) {
                    await wait(200);
                }
                chunkList.forEach(item => {
                    const { _lastUpdateTime, enterpriseId: _enterpriseId, orgId, id } = item;
                    if (_enterpriseId && _enterpriseId !== enterpriseId) {
                        return;
                    }
                    if (orgId && !/^\d+/.test(orgId)) {
                        return;
                    }
                    if (!_lastUpdateTime || _lastUpdateTime < lastUpdateTime) {
                        needDeleteOrgContactIds.push(id);
                    }
                });
                return true;
            });
            return total;
        }, Promise.resolve(true));
        if (!needDeleteOrgContactIds || !needDeleteOrgContactIds.length) {
            return;
        }
        this.ctx.dataTrackerApi.track('pc_contact_check_duplicatedata', {
            _account: params._account,
            recordSubAccount: false,
            count: needDeleteOrgContactIds.length,
            type: 'user',
        });
        this.ctx.contactUtil.forbiddenBridgeOnce();
        await this.ctx.dbApi.deleteById({
            dbName: this.ctx.contactUtil.contactDbName,
            tableName: this.ctx.contactUtil.orgContactTable,
            _dbAccount: params._account,
        }, needDeleteOrgContactIds);
    }
    async deleteInvalidCoreContact(options) {
        const { lastUpdateTime, enterpriseId, _account: _dbAccount } = options;
        const timesstamp = lastUpdateTime - 10;
        const promiseRequestFunctionList = [
            () => {
                this.ctx.contactUtil.forbiddenBridgeOnce();
                return this.ctx.dbApi.deleteByByRangeCondition({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.contactTable,
                    adCondition: {
                        type: 'between',
                        field: ['enterpriseId', '_lastUpdateTime'],
                        args: [[enterpriseId, 0], [enterpriseId, timesstamp], true],
                    },
                    _dbAccount,
                }, _dbAccount, { supportCache: false });
            },
            () => {
                this.ctx.contactUtil.forbiddenBridgeOnce();
                return this.ctx.dbApi.deleteByByRangeCondition({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.contactItemTable,
                    adCondition: {
                        type: 'between',
                        field: ['contactItemType', 'enterpriseId', '_lastUpdateTime'],
                        args: [['EMAIL', enterpriseId, 0], ['EMAIL', enterpriseId, timesstamp], true],
                    },
                    _dbAccount,
                }, _dbAccount, { supportCache: false });
            },
            () => {
                this.ctx.contactUtil.forbiddenBridgeOnce();
                return this.ctx.dbApi.deleteByByRangeCondition({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.orgContactTable,
                    adCondition: {
                        type: 'between',
                        field: ['enterpriseId', '_lastUpdateTime'],
                        args: [[enterpriseId, 0], [enterpriseId, timesstamp], true],
                    },
                    _dbAccount,
                }, _dbAccount, { supportCache: false });
            },
        ];
        return Promise.all(promiseRequestFunctionList.map(func => func()))
            .then(() => this.deleteDuplicateOrgContact({ lastUpdateTime: timesstamp, enterpriseId, _account: options._account }))
            .finally(() => {
            this.sendCoreDeleteInvalidEvent({
                tableName: 'contact',
                enterpriseId,
                _account: options._account,
            });
        });
    }
    async sendCoreDeleteInvalidEvent(params) {
        var _a, _b, _c;
        if (inWindow() && window.isBridgeWorker) {
            return;
        }
        const mainCompanyId = this.ctx.systemApi.getCurrentCompanyId(params._account);
        if (params.enterpriseId !== mainCompanyId) {
            return;
        }
        (_a = this.coreNeedDeleteStep.get(params._account)) === null || _a === void 0 ? void 0 : _a.delete(params.tableName);
        if (!((_b = this.coreNeedDeleteStep.get(params._account)) === null || _b === void 0 ? void 0 : _b.size)) {
            this.ctx.contactUtil.sendCrossEvent({
                eventName: 'changeCoreContactSyncStatus',
                eventData: {
                    status: 'cleanDone',
                },
                _account: ((_c = this.ctx.systemApi.getCurrentUser(params._account)) === null || _c === void 0 ? void 0 : _c.id) || '',
            });
        }
    }
    async detectCoreEnterpriseHasData(options) {
        const { from, subDomain, _account } = options;
        const currentUser = this.ctx.systemApi.getCurrentUser(_account);
        const accountMd5 = lodashGet(currentUser, 'accountMd5', '');
        const taskApi = this.ctx.taskApi;
        const curDomain = subDomain || lodashGet(currentUser, 'domain') || lodashGet(currentUser, 'id', '').replace(/^.+@/, '');
        let taskList;
        try {
            taskList = await this.ctx.dbApi.getByRangeCondition({
                dbName: 'task_global',
                tableName: 'contact_synctask',
            });
        }
        catch (ex) {
            console.error('[contactDB]detectCoreEnterpriseHasData.error1:', ex);
            return 'none';
        }
        if (!Array.isArray(taskList) || taskList.length <= 0) {
            return 'none';
        }
        const stepMap = from
            ? new Map([[from, 'none']])
            : new Map([
                ['personal', 'none'],
                ['personalOrg', 'none'],
                ['enterprise', 'none'],
                ['org', 'none'],
            ]);
        taskList.forEach(item => {
            if (item.account !== accountMd5 || item.domain !== curDomain || !stepMap.has(item.from)) {
                return;
            }
            const enterpriseStep = taskApi.getContactDoneStep(item);
            stepMap.set(item.from, enterpriseStep);
        });
        let taskStep = 'none';
        const stepList = [...stepMap.values()];
        if (stepList.includes('none')) {
            return 'none';
        }
        if (stepList.includes('core')) {
            taskStep = 'core';
        }
        else {
            taskStep = 'all';
        }
        if (from === 'personalOrg' || from === 'personal') {
            this.setTempContactSyncStatus(taskStep, from);
            return taskStep;
        }
        const totalRequestList = new Map([
            [
                'enterprise',
                {
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.contactTable,
                    query: {
                        type: 'enterprise',
                    },
                    count: 1,
                    _account: options._account,
                },
            ],
            [
                'org',
                {
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.orgTable,
                    adCondition: {
                        type: 'anyOf',
                        field: 'type',
                        args: [0, 99],
                    },
                    count: 1,
                    _account: options._account,
                },
            ],
        ]);
        const requestPromise = (from ? [totalRequestList.get(from)] : [...totalRequestList.values()]).map(item => {
            this.ctx.contactUtil.forbiddenBridgeOnce();
            const queryApiName = (item === null || item === void 0 ? void 0 : item.query) ? 'getByEqCondition' : 'getByRangeCondition';
            return this.ctx.dbApi[queryApiName](item);
        });
        try {
            const list = await Promise.all(requestPromise);
            const step = list.flat().length >= (from ? 1 : 2) ? taskStep : 'none';
            this.setTempContactSyncStatus(step, from);
            return step;
        }
        catch (ex) {
            return 'none';
        }
    }
    async detectTempContactSyncStatusWithMemory(from) {
        const key = from || 'all';
        if (!this.contactTempSyncStatus.has(key)) {
            return this.detectCoreEnterpriseHasData({ from });
        }
        const { status, expiredTime } = this.contactTempSyncStatus.get(key);
        if (Date.now() > expiredTime) {
            return this.detectCoreEnterpriseHasData({ from });
        }
        return status;
    }
    setTempContactSyncStatus(status, from) {
        if (status === 'none') {
            return;
        }
        this.contactTempSyncStatus.set(from || 'all', {
            status,
            expiredTime: Date.now() + 20 * 60 * 1000,
        });
    }
    async deleteInvalidCoreContactPublic(options) {
        return this.deleteInvalidCoreContact(options);
    }
    setNeedComputeMemberNum(options) {
        this.needComputeMemberNum.set(options._account, options.flag);
    }
    async triggerComputeEntityOrgMemeberNum(account) {
        let needCompute = this.needComputeMemberNum.get(account) || false;
        if (!needCompute) {
            const orgList = await this.ctx.dbApi.getByEqCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgTable,
                query: {
                    id: '-1',
                },
            });
            const rootMemberNumber = lodashGet(orgList, '[0].memberNum', 0);
            needCompute = typeof rootMemberNumber !== 'number' || rootMemberNumber <= 0;
        }
        if (!needCompute) {
            return;
        }
        const totalOrgContactList = (await this.ctx.dbApi.getByEqCondition({
            dbName: this.ctx.contactUtil.contactDbName,
            tableName: this.ctx.contactUtil.orgContactTable,
            query: {
                type: 'enterprise',
            },
        }));
        const totalOrgList = await this.ctx.dbApi.getByRangeCondition({
            dbName: this.ctx.contactUtil.contactDbName,
            tableName: this.ctx.contactUtil.orgTable,
            adCondition: {
                type: 'anyOf',
                field: 'type',
                args: [0, 1, 2, 99],
            },
        });
        const orgContactMap = new Map();
        const orgTreeMap = new Map();
        totalOrgList.forEach(item => {
            const { id, parent } = item;
            if (!orgTreeMap.has(parent)) {
                orgTreeMap.set(parent, []);
            }
            orgTreeMap.get(parent).push(id);
        });
        totalOrgContactList.forEach(item => {
            const { orgId } = item;
            orgContactMap.set(orgId, (orgContactMap.get(orgId) || 0) + 1);
        });
        const nodeMemberCountMap = await this.ctx.contactUtil.computeEntityOrgMemberCount({
            rootOrgKey: '-2',
            orgContactMap,
            orgTreeMap,
        });
        const needUpdateOrgList = [];
        totalOrgList.forEach(item => {
            const { id, memberNum = 0 } = item;
            const { totalCount } = nodeMemberCountMap.get(id) || { totalCount: 0 };
            if (memberNum === totalCount) {
                return;
            }
            needUpdateOrgList.push(Object.assign(Object.assign({}, item), { memberNum: totalCount }));
        });
        if (needUpdateOrgList && needUpdateOrgList.length) {
            await this.ctx.dbApi.putAll({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgTable,
            }, needUpdateOrgList);
        }
        this.needComputeMemberNum.set(account, false);
    }
    cleanInvalidEnterpriseRawData(options) {
        const { contactIds, _account } = options;
        const queryIds = async (ids) => {
            const [contactItemList, orgContactList] = (await Promise.all([this.ctx.contactUtil.contactItemTable, this.ctx.contactUtil.orgContactTable].map(tableName => this.ctx.dbApi.getByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName,
                adCondition: {
                    type: 'anyOf',
                    field: 'contactId',
                    args: [ids],
                },
                _dbAccount: _account,
            }))));
            return [contactItemList.map(item => item.id), orgContactList.map(item => item.id)];
        };
        const deleteRawByIds = async (tableName, ids) => {
            if (!Array.isArray(ids) || !ids.length) {
                return;
            }
            await this.ctx.dbApi.deleteById({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName,
                _dbAccount: _account,
            }, ids);
        };
        return lodashChunk(contactIds, 1000).reduce(async (total, current, index) => {
            total = total
                .then(async () => {
                if (index > 0) {
                    await wait(100);
                }
                return queryIds(current);
            })
                .then(async ([chunkContactItemIds, chunkOrgContactIds]) => {
                await Promise.all([
                    deleteRawByIds(this.ctx.contactUtil.contactTable, current),
                    deleteRawByIds(this.ctx.contactUtil.contactItemTable, chunkContactItemIds),
                    deleteRawByIds(this.ctx.contactUtil.orgContactTable, chunkOrgContactIds),
                ]);
            })
                .catch(ex => {
                console.error('[conactDB]cleanInvalidEnterpriseData.error', ex);
            });
            return total;
        }, Promise.resolve());
    }
    cleanExpiredEntityRawdata(options) {
        const { list: contactModelList, lastUpdateTime, _account } = options;
        const timestamp = lastUpdateTime - 10;
        const queryExpiredList = async (ids, timestamp) => {
            const [contactItemList, orgContactList] = await Promise.all([
                this.ctx.dbApi.getByRangeCondition({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.contactItemTable,
                    adCondition: {
                        type: 'anyOf',
                        field: 'contactId',
                        args: [ids],
                    },
                    _dbAccount: _account,
                }),
                this.ctx.dbApi.getByRangeCondition({
                    dbName: this.ctx.contactUtil.contactDbName,
                    tableName: this.ctx.contactUtil.orgContactTable,
                    adCondition: {
                        type: 'anyOf',
                        field: 'contactId',
                        args: [ids],
                    },
                    _dbAccount: _account,
                }),
            ]);
            const expiredOrgContactIds = [];
            const expiredContactItemIds = [];
            contactItemList.forEach(item => {
                if (item._lastUpdateTime >= timestamp) {
                    return;
                }
                expiredContactItemIds.push(item.id);
            });
            orgContactList.forEach(item => {
                if (item._lastUpdateTime >= timestamp) {
                    return;
                }
                expiredOrgContactIds.push(item.id);
            });
            return { expiredContactItemIds, expiredOrgContactIds };
        };
        const deleteExpiredList = async (idsMap) => {
            const { expiredContactItemIds, expiredOrgContactIds } = idsMap;
            await Promise.all([
                expiredContactItemIds && expiredContactItemIds.length
                    ? this.ctx.dbApi.deleteById({
                        dbName: this.ctx.contactUtil.contactDbName,
                        tableName: this.ctx.contactUtil.contactItemTable,
                        _dbAccount: _account,
                    }, expiredContactItemIds)
                    : Promise.resolve(),
                expiredOrgContactIds && expiredOrgContactIds.length
                    ? this.ctx.dbApi.deleteById({
                        dbName: this.ctx.contactUtil.contactDbName,
                        tableName: this.ctx.contactUtil.orgContactTable,
                        _dbAccount: _account,
                    }, expiredOrgContactIds)
                    : Promise.resolve(),
            ]);
        };
        return lodashChunk(contactModelList, 1000).reduce((total, current, index) => {
            total = total
                .then(async () => {
                if (index) {
                    await wait(100);
                }
                return queryExpiredList(current.map(item => item.id), timestamp);
            })
                .then(deleteExpiredList)
                .catch(ex => {
                console.error('[conactDB]cleanExpiredEntityRawdata.error', ex);
            });
            return total;
        }, Promise.resolve());
    }
    async hasSubDomainData(orgId) {
        console.log('[contactDBL]hasSubDomainData:', orgId);
        const list = await this.ctx.dbApi.getByEqCondition({
            dbName: this.ctx.contactUtil.contactDbName,
            tableName: this.ctx.contactUtil.orgTable,
            query: {
                originId: `org_${orgId}`,
            },
            count: 1,
        });
        return list.length > 0;
    }
    async deleteCustomerDataInDB() {
        try {
            const res = await this.ctx.dbApi.deleteDB(this.ctx.contactUtil.contactCustomerGlobalDBName);
            console.log('delete customer data succeeded', res);
        }
        catch (err) {
            console.log('delete customer data failed');
        }
    }
    sendContactNotify(res, contactSyncTimesRef, setLastUpdateTime) {
        setLastUpdateTime();
        contactSyncTimesRef.value += 1;
        res.contactSyncTimes = contactSyncTimesRef.value;
        res.hasDiff = true;
        dispatchContactSyncEvents(this.ctx.eventApi, res, this.ctx.systemApi);
    }
    init(inited, initSyncNIM, initContactData, testDbNoData, _setEnablePutContact) {
        this.ctx.dbApi.initDb(this.ctx.contactUtil.contactDbName);
        console.log('[contact] contactDB inited', inited);
        if (!inited) {
            initSyncNIM();
            testDbNoData().then(noData => {
                if (!noData) {
                    initContactData();
                }
            });
            return true;
        }
        return inited;
    }
    async contactDetailInto(lokiApi, contacts, contactItems, lxInfoRows, _account) {
        if (!contacts.length) {
            return;
        }
        const writePromises = [
            writeContactsToLoki(lokiApi, contacts, contactItems, _account),
            this.ctx.dbApi.putAll({ dbName: this.ctx.contactUtil.contactDbName, tableName: this.ctx.contactUtil.contactTable, _dbAccount: _account }, contacts, { pageSize: 5000, supportCache: 'disable' }),
            this.ctx.dbApi.putAll({ dbName: this.ctx.contactUtil.contactDbName, tableName: this.ctx.contactUtil.contactItemTable, _dbAccount: _account }, contactItems, { pageSize: 5000, supportCache: 'disable' }),
        ];
        if (lxInfoRows.length) {
            const lokiLxInfoRows = lxInfoRows.map(row => ({
                contactId: row.contactId,
                avatar: row.avatar || '',
                yxid: row.yxid || '',
                yxToken: row.yxToken || '',
            }));
            writePromises.push(writeLxInfoToLoki(lokiApi, lokiLxInfoRows, _account), this.ctx.dbApi.putAll({ dbName: this.ctx.contactUtil.contactDbName, tableName: this.ctx.contactUtil.lxInfoTable, _dbAccount: _account }, lxInfoRows, { supportCache: 'disable' }));
        }
        await Promise.all(writePromises);
    }
    async searchResultInto(lokiApi, contacts, contactItems, _account) {
        if (!contacts.length) {
            return;
        }
        await Promise.all([
            writeContactsToLoki(lokiApi, contacts, contactItems, _account),
            this.ctx.dbApi.putAll({ dbName: this.ctx.contactUtil.contactDbName, tableName: this.ctx.contactUtil.contactTable, _dbAccount: _account }, contacts, { pageSize: 5000, supportCache: 'disable' }),
            this.ctx.dbApi.putAll({ dbName: this.ctx.contactUtil.contactDbName, tableName: this.ctx.contactUtil.contactItemTable, _dbAccount: _account }, contactItems, { pageSize: 5000, supportCache: 'disable' }),
        ]);
    }
    async writeNewEnterpriseToDexie(params) {
        var _a, _b;
        const { orgs, contacts, contactItems, orgContacts, _account } = params;
        const dbName = this.ctx.contactUtil.contactDbName;
        const tracker = this.ctx.tracker;
        const sessionId = (_b = (_a = tracker === null || tracker === void 0 ? void 0 : tracker.getCurrentSession()) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
        const makeProgress = (tableName, total) => {
            if (!tracker)
                return { supportCache: false };
            tracker.updateDexieProgress(sessionId, 'enterprise', tableName, { total, written: 0, status: 'writing' });
            return {
                supportCache: false,
                onProgress: (info) => {
                    tracker.updateDexieProgress(sessionId, 'enterprise', tableName, {
                        written: info.writtenSoFar,
                        total: info.totalItems,
                        status: info.writtenSoFar >= info.totalItems ? 'done' : 'writing',
                    });
                },
            };
        };
        await Promise.all([
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.contactTable, _dbAccount: _account }, contacts, makeProgress(this.ctx.contactUtil.contactTable, contacts.length), _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.contactItemTable, _dbAccount: _account }, contactItems, makeProgress(this.ctx.contactUtil.contactItemTable, contactItems.length), _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.orgContactTable, _dbAccount: _account }, orgContacts, makeProgress(this.ctx.contactUtil.orgContactTable, orgContacts.length), _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.orgTable, _dbAccount: _account }, orgs, makeProgress(this.ctx.contactUtil.orgTable, orgs.length), _account),
        ]);
    }
    async deleteOldNewEnterpriseData(params) {
        const { enterpriseId, lastUpdateTime, _account } = params;
        const dbName = this.ctx.contactUtil.contactDbName;
        const supportCacheOff = { supportCache: false };
        const ts = lastUpdateTime - 10;
        const makeCondition = (tableName) => ({
            dbName,
            tableName,
            adCondition: {
                type: 'between',
                field: ['enterpriseId', '_lastUpdateTime'],
                args: [[enterpriseId, 0], [enterpriseId, ts], true],
            },
            _dbAccount: _account,
        });
        try {
            const oldContacts = await this.ctx.dbApi.getByRangeCondition(makeCondition(this.ctx.contactUtil.contactTable), _account);
            const oldContactIds = (oldContacts || []).map(c => c.id).filter(Boolean);
            await Promise.all([
                this.ctx.dbApi.deleteByByRangeCondition(makeCondition(this.ctx.contactUtil.contactTable), _account, supportCacheOff),
                ...oldContactIds.map(contactId => this.ctx.dbApi
                    .deleteByByRangeCondition({
                    dbName,
                    tableName: this.ctx.contactUtil.contactItemTable,
                    adCondition: { type: 'equals', field: 'contactId', args: [contactId] },
                    _dbAccount: _account,
                }, _account, supportCacheOff)
                    .catch(() => {
                })),
                this.ctx.dbApi.deleteByByRangeCondition(makeCondition(this.ctx.contactUtil.orgContactTable), _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition(makeCondition(this.ctx.contactUtil.orgTable), _account, supportCacheOff),
            ]);
        }
        catch (ex) {
            console.error('[ContactDBEnterprise] deleteOldNewEnterpriseData failed', ex);
        }
    }
    async deleteOldEnterpriseDataByType(params) {
        const { lastUpdateTime, _account } = params;
        const dbName = this.ctx.contactUtil.contactDbName;
        const supportCacheOff = { supportCache: false };
        const ts = lastUpdateTime - 10;
        const makeEnterpriseCondition = (tableName) => ({
            dbName,
            tableName,
            adCondition: {
                type: 'between',
                field: ['type', '_lastUpdateTime'],
                args: [['enterprise', 0], ['enterprise', ts], true],
            },
            _dbAccount: _account,
        });
        try {
            await Promise.all([
                this.ctx.dbApi.deleteByByRangeCondition(makeEnterpriseCondition(this.ctx.contactUtil.contactTable), _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition(makeEnterpriseCondition(this.ctx.contactUtil.contactItemTable), _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition(makeEnterpriseCondition(this.ctx.contactUtil.orgContactTable), _account, supportCacheOff),
            ]);
        }
        catch (ex) {
            console.error('[ContactDBEnterprise] deleteOldEnterpriseDataByType failed', ex);
        }
    }
    async deleteAllEnterpriseOrgsFromDexie(params) {
        const { _account } = params;
        try {
            await this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgTable,
                adCondition: {
                    type: 'below',
                    field: 'type',
                    args: [2000],
                },
                _dbAccount: _account,
            }, _account, { supportCache: false });
        }
        catch (ex) {
            console.error('[ContactDBEnterprise] deleteAllEnterpriseOrgsFromDexie failed', ex);
        }
    }
    async writeOrgContactsOnDemandToDexie(params) {
        const { orgIds, contacts, contactItems, orgContacts, _account } = params;
        const dbName = this.ctx.contactUtil.contactDbName;
        const supportCacheOff = { supportCache: false };
        await Promise.all(orgIds.map(orgId => this.ctx.dbApi
            .deleteByByRangeCondition({
            dbName,
            tableName: this.ctx.contactUtil.orgContactTable,
            adCondition: { type: 'equals', field: 'orgId', args: [orgId] },
            _dbAccount: _account,
        }, _account, supportCacheOff)
            .catch(ex => {
            console.warn(`[ContactDBEnterprise] writeOrgContactsOnDemandToDexie: delete orgContact failed for orgId=${orgId}`, ex);
        })));
        await Promise.all([
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.orgContactTable, _dbAccount: _account }, orgContacts, supportCacheOff, _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.contactTable, _dbAccount: _account }, contacts, supportCacheOff, _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.contactItemTable, _dbAccount: _account }, contactItems, supportCacheOff, _account),
        ]);
    }
    async deleteAllEnterpriseContactsFromDexie(enterpriseId, _account) {
        const dbName = this.ctx.contactUtil.contactDbName;
        const supportCacheOff = { supportCache: false };
        try {
            const enterpriseContacts = await this.ctx.dbApi.getByRangeCondition({
                dbName,
                tableName: this.ctx.contactUtil.contactTable,
                adCondition: {
                    type: 'between',
                    field: ['enterpriseId', '_lastUpdateTime'],
                    args: [[enterpriseId, 0], [enterpriseId, Infinity], true],
                },
                _dbAccount: _account,
            }, _account);
            const contactIds = (enterpriseContacts || []).map(c => c.id).filter(Boolean);
            await Promise.all([
                this.ctx.dbApi.deleteByByRangeCondition({
                    dbName,
                    tableName: this.ctx.contactUtil.contactTable,
                    adCondition: {
                        type: 'between',
                        field: ['enterpriseId', '_lastUpdateTime'],
                        args: [[enterpriseId, 0], [enterpriseId, Infinity], true],
                    },
                    _dbAccount: _account,
                }, _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition({
                    dbName,
                    tableName: this.ctx.contactUtil.contactItemTable,
                    adCondition: {
                        type: 'anyOf',
                        field: 'contactId',
                        args: [contactIds],
                    },
                    _dbAccount: _account,
                }, _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition({
                    dbName,
                    tableName: this.ctx.contactUtil.orgContactTable,
                    adCondition: {
                        type: 'between',
                        field: ['enterpriseId', '_lastUpdateTime'],
                        args: [[enterpriseId, 0], [enterpriseId, Infinity], true],
                    },
                    _dbAccount: _account,
                }, _account, supportCacheOff),
            ]);
            console.log(`[ContactDBEnterprise] deleteAllEnterpriseContactsFromDexie done, enterpriseId=${enterpriseId}`);
        }
        catch (ex) {
            console.error('[ContactDBEnterprise] deleteAllEnterpriseContactsFromDexie failed', ex);
        }
    }
    async deleteAllEnterpriseContactsByType(params) {
        const { _account } = params;
        const dbName = this.ctx.contactUtil.contactDbName;
        const supportCacheOff = { supportCache: false };
        const makeCondition = (tableName) => ({
            dbName,
            tableName,
            adCondition: {
                type: 'between',
                field: ['type', '_lastUpdateTime'],
                args: [['enterprise', 0], ['enterprise', Infinity], true],
            },
            _dbAccount: _account,
        });
        try {
            await Promise.all([
                this.ctx.dbApi.deleteByByRangeCondition(makeCondition(this.ctx.contactUtil.contactTable), _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition(makeCondition(this.ctx.contactUtil.contactItemTable), _account, supportCacheOff),
                this.ctx.dbApi.deleteByByRangeCondition(makeCondition(this.ctx.contactUtil.orgContactTable), _account, supportCacheOff),
            ]);
            console.log(`[ContactDBEnterprise] deleteAllEnterpriseContactsByType done`);
        }
        catch (ex) {
            console.error('[ContactDBEnterprise] deleteAllEnterpriseContactsByType failed', ex);
        }
    }
    async writeContactsToDexie(params) {
        const { contacts, contactItems, orgContacts, _account } = params;
        const dbName = this.ctx.contactUtil.contactDbName;
        const supportCacheOff = { supportCache: false };
        await Promise.all([
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.contactTable, _dbAccount: _account }, contacts, supportCacheOff, _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.contactItemTable, _dbAccount: _account }, contactItems, supportCacheOff, _account),
            this.ctx.dbApi.putAll({ dbName, tableName: this.ctx.contactUtil.orgContactTable, _dbAccount: _account }, orgContacts, supportCacheOff, _account),
        ]);
    }
}
